import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import HotRanking from "@/pages/home/components/HotRanking";
import usePageMeta from "@/hooks/usePageMeta";
import { formatDate, formatViews, getCategoryName, getHotNews, getNewsById, getRelatedNews } from "@/utils/news";

export default function DetailPage() {
  const { newsId = "" } = useParams();
  const navigate = useNavigate();
  const item = getNewsById(newsId);

  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [newsId]);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 2200);
    return () => window.clearTimeout(id);
  }, [toast]);

  const related = useMemo(() => (item ? getRelatedNews(item, 4) : []), [item]);
  const hotNews = useMemo(() => getHotNews(6), []);

  usePageMeta({
    title: item ? `${item.title} - 吃瓜爆料` : "新闻详情 - 吃瓜爆料",
    description: item ? item.summary : "吃瓜爆料新闻详情页。",
    keywords: item ? `${item.tags.join(",")},${getCategoryName(item.categoryId)},吃瓜爆料` : "吃瓜爆料,新闻详情",
  });

  const handleCopy = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="w-full px-4 md:px-8 py-8">
          <Breadcrumb items={[{ label: "新闻详情" }]} />
          <EmptyState
            icon="ri-file-warning-line"
            title="新闻已下线或不存在"
            description="这条爆料可能已被撤下，或者链接有误。你可以返回首页继续浏览其他热点。"
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-1 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-home-4-line" /> 返回首页
              </Link>
            }
          />
        </main>
        <Footer />
      </div>
    );
  }

  const firstSentence = item.content[0]?.split("。")[0] ?? "";
  const restOfFirst = item.content[0]?.split("。").slice(1).join("。") ?? "";

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="w-full px-4 md:px-8 py-6">
        <Breadcrumb
          items={[
            { label: getCategoryName(item.categoryId), to: `/category/${item.categoryId}` },
            { label: "正文" },
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2 min-w-0">
            <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7">
              <h1 className="text-2xl md:text-3xl font-black text-foreground-950 leading-snug">{item.title}</h1>

              <div className="flex items-center gap-3 flex-wrap mt-4 pb-5 border-b border-background-200 text-xs md:text-sm text-foreground-500">
                <Link
                  to={`/category/${item.categoryId}`}
                  className="inline-flex items-center gap-1 bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full whitespace-nowrap hover:bg-primary-200 cursor-pointer"
                >
                  <i className="ri-bookmark-line" />
                  {getCategoryName(item.categoryId)}
                </Link>
                <span className="whitespace-nowrap">{item.subCategory}</span>
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-user-line" /> {item.author}
                </span>
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-price-tag-3-line" /> {item.source}
                </span>
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-time-line" /> {formatDate(item.publishTime)}
                </span>
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-eye-line" /> {formatViews(item.views)}
                </span>
              </div>

              <div className="relative w-full h-56 md:h-96 rounded-lg overflow-hidden bg-background-100 my-5">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - 吃瓜爆料`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="space-y-4 text-base md:text-[17px] leading-8 text-foreground-800">
                <p>
                  {firstSentence && <strong className="text-foreground-950">{firstSentence}。</strong>}
                  {restOfFirst}
                </p>
                {item.content.slice(1).map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-6 pt-5 border-t border-background-200">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-6 p-4 rounded-lg bg-background-100">
                <span className="text-sm text-foreground-600 whitespace-nowrap">分享这条爆料：</span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-link" /> 复制链接
                </button>
                {[
                  { icon: "ri-wechat-line", label: "微信" },
                  { icon: "ri-weibo-line", label: "微博" },
                  { icon: "ri-share-forward-line", label: "更多" },
                ].map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => setToast("演示环境：暂未接入第三方分享平台")}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-background-300 text-sm text-foreground-700 whitespace-nowrap hover:bg-background-50 cursor-pointer"
                  >
                    <i className={s.icon} /> {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button
                type="button"
                onClick={() => navigate(`/category/${item.categoryId}`)}
                className="inline-flex items-center gap-1 h-10 px-5 rounded-md bg-background-100 text-foreground-800 text-sm font-medium whitespace-nowrap hover:bg-background-200 cursor-pointer"
              >
                <i className="ri-arrow-left-line" /> 返回分类页
              </button>
              <Link
                to="/"
                className="inline-flex items-center gap-1 h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-home-4-line" /> 返回首页
              </Link>
            </div>

            <section className="mt-10">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1.5 h-6 rounded-full bg-primary-500" />
                <h2 className="text-xl font-bold text-foreground-950">相关阅读推荐</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/detail/${r.id}`}
                    className="group flex gap-3 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 cursor-pointer transition-colors"
                  >
                    <span className="shrink-0 w-24 h-20 rounded-md overflow-hidden bg-background-100">
                      <img
                        src={r.cover}
                        alt={r.title}
                        title={`${r.title} - 吃瓜爆料`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {r.title}
                      </span>
                      <span className="block text-xs text-foreground-400 mt-2">{formatDate(r.publishTime)}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          </article>

          <aside className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" />
              <h2 className="text-lg font-bold text-foreground-950">热门爆料榜</h2>
            </div>
            <HotRanking items={hotNews} />
          </aside>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      )}

      <Footer />
    </div>
  );
}